/**
 * The three screens that close a chapter (spec 9.5), all ordinary feed cards reached by
 * scrolling. Ported from the checkpoint prototype's shared screens, with one deliberate change
 * the founder decided on 2026-09-22: below 4 of 5 the line is «Nächstes Mal klappt es besser»
 * (build spec §1) rather than the prototype's «Fast!».
 */

import type { AttemptResult } from "../../shared/api-types.js";

const CONFETTI_COLOURS = ["#ffd23f", "#ffffff", "#2ecc71", "#e74c3c"];

function GoalRow({ today, dayGoal }: { today: number; dayGoal: number }) {
  return (
    <div className="mt-7 flex justify-center gap-2.5">
      {Array.from({ length: dayGoal }, (_, index) => {
        const done = index < today;
        return (
          <i
            key={index}
            className={
              "grid h-11 w-11 place-items-center rounded-full border-2 text-[16px] font-extrabold not-italic " +
              (done
                ? "border-[var(--color-accent)] bg-[var(--color-accent)] text-black"
                : "border-white/30 text-white")
            }
          >
            {done ? "✓" : index + 1}
          </i>
        );
      })}
    </div>
  );
}

function Hint({ children }: { children: string }) {
  return (
    <div
      className="absolute inset-x-0 bottom-7 text-center text-[13px] text-white opacity-60"
      style={{ bottom: "calc(1.75rem + env(safe-area-inset-bottom, 0px))" }}
    >
      {children}
    </div>
  );
}

export function ResultScreen({ result, dayGoal }: { result: AttemptResult; dayGoal: number }) {
  return (
    <section className="feed-screen result-bg grid place-items-center p-10 text-center">
      <div className="relative">
        <div className="text-[96px] leading-none font-black text-[var(--color-accent)]">
          {result.score}/5
        </div>
        <h2 className="mt-1.5 mb-1 text-[26px] font-extrabold text-white">
          {result.learned ? "Gelernt!" : "Nächstes Mal klappt es besser"}
        </h2>
        {/* «Das Thema» first: a bare «Jesus kommt später nochmal.» reads as a statement about
            Jesus, not about the topic (founder, 2026-09-27). */}
        <p className="m-0 text-white opacity-75">
          {result.learned
            ? "Das Thema «" + result.topicTitle + "» ist geschafft."
            : "Das Thema «" + result.topicTitle + "» kommt später nochmal."}
        </p>
        <GoalRow today={result.today} dayGoal={dayGoal} />
      </div>
      <Hint>↓ weiter scrollen</Hint>
    </section>
  );
}

export function CelebrationScreen({ dayGoal }: { dayGoal: number }) {
  return (
    <section className="feed-screen celebrate-bg grid place-items-center p-10 text-center">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {Array.from({ length: 24 }, (_, index) => (
          <i
            key={index}
            className="confetti-piece"
            style={{
              left: ((index * 4.2) % 100) + "%",
              animationDelay: ((index * 137) % 3000) + "ms",
              background: CONFETTI_COLOURS[index % CONFETTI_COLOURS.length],
            }}
          />
        ))}
      </div>
      <div className="relative">
        <div className="text-[96px] leading-none">🏆</div>
        <h2 className="mt-3 mb-1.5 text-[34px] font-black text-white">Tagesziel erreicht!</h2>
        <p className="m-0 text-white opacity-80">
          {dayGoal} Themen heute. Stark.
          <br />
          Du kannst weitermachen, so viel du willst.
        </p>
      </div>
      <Hint>↓ weiter</Hint>
    </section>
  );
}

export function AllReadScreen() {
  return (
    <section className="feed-screen grid place-items-center bg-[#0d0d0d] p-10 text-center">
      <div className="relative">
        <div className="text-[72px] leading-none">📚</div>
        <h2 className="mt-3 mb-1.5 text-[30px] font-extrabold text-white">Alles gelesen!</h2>
        <p className="m-0 max-w-[280px] text-white opacity-70">
          Du hast alle Geschichte-Themen durch. Ab jetzt kommen Themen, die du schon kennst. Zum
          Auffrischen.
        </p>
      </div>
      <Hint>↓ weiter</Hint>
    </section>
  );
}
